import { apiAuthCaller } from '@services/apiCaller';
import { action, computed, observable } from 'mobx';
import { analytics } from '@lib/analytics';
import { CATEGORY, TRACK_TYPE } from '@lib/analyticsEvents';
import { IRootStore } from '.';
import { getApiErrorMessage } from '@components/common/util/util';

type Seed = {
  id: string;
  name: string;
  enforcedMfa: boolean;
  freetrial: boolean;
  pro: boolean;
  enterprise: boolean;
};

export interface IOrganization {
  id: string;
  name: string;
  enforcedMfa: boolean;
  isFreeTrial: boolean;
  isPro: boolean;
  isEnterprise: boolean;
  isOpenSource: boolean;
  updateEnforcedMfa: (val: boolean) => void;
  updateName: (val: string) => Promise<boolean>;
  seed: (obj: Seed) => void;
  prettyPrintTier: () => string;
  reset: () => void;
}

enum Tier {
  OPENSOURCE,
  FREETRIAL,
  PRO,
  ENTERPRISE,
  NONE,
}

export class OrganizationStore implements IOrganization {
  constructor(protected rootStore: IRootStore) {}

  @observable public id = '';

  @observable public name = '';

  @observable public enforcedMfa = false;

  @observable private tier = Tier.NONE;

  @action.bound
  seed(data: Seed) {
    this.id = data.id;
    this.name = data.name;
    this.enforcedMfa = data.enforcedMfa;
    this.tier = Math.max(
      data.freetrial ? Tier.FREETRIAL : 0,
      data.pro ? Tier.PRO : 0,
      data.enterprise ? Tier.ENTERPRISE : 0,
      !(data.freetrial && data.pro && data.enterprise) ? Tier.OPENSOURCE : 0,
    );
  }

  @computed get isOpenSource() {
    return this.tier === Tier.OPENSOURCE;
  }

  @computed get isFreeTrial() {
    return this.tier === Tier.FREETRIAL;
  }

  @computed get isPro() {
    return this.tier === Tier.PRO;
  }

  @computed get isEnterprise() {
    return this.tier === Tier.ENTERPRISE;
  }

  @action.bound
  async updateName(name: string) {
    const BI_SUCCESS_MESSAGE = 'Organization name updated successfully';
    const BI_FAILURE_MESSAGE = 'Organization name update failure';
    const {
      userStore: { token, email },
      messagesStore: { showErrorMessage },
    } = this.rootStore;
    try {
      await apiAuthCaller(token).post(`/organizations/${this.id}/updateName`, { name });
      this.name = name;
      analytics.track(BI_SUCCESS_MESSAGE, TRACK_TYPE.API_SUCCESS, CATEGORY.AUTH);
      return true;
    } catch (error) {
      showErrorMessage(getApiErrorMessage(error, 'Failed to update company name'));
      analytics.track(BI_FAILURE_MESSAGE, TRACK_TYPE.API_ERROR, CATEGORY.AUTH, {
        name,
        email,
        error,
      });
    }

    return false;
  }

  @action.bound
  async updateEnforcedMfa(enforcedMfa: boolean) {
    const BI_SUCCESS_MESSAGE = 'Organization enforced MFA updated successfully';
    const BI_FAILURE_MESSAGE = 'Organization enforced MFA update failure';
    const {
      userStore: { token, email, updateEnforcedMfa },
      messagesStore,
    } = this.rootStore;
    try {
      await apiAuthCaller(token).post(`/organizations/${this.id}/enforceMfa`, { enforcedMfa });
      this.enforcedMfa = enforcedMfa;
      await updateEnforcedMfa();
      messagesStore.showSuccessMessage('Successfully updated enforced MFA option');
      analytics.track(BI_SUCCESS_MESSAGE, TRACK_TYPE.API_SUCCESS, CATEGORY.AUTH);
    } catch (error) {
      messagesStore.showErrorMessage('Failed to update enforced MFA option');
      // TODO: what category does this belong to?
      analytics.track(BI_FAILURE_MESSAGE, TRACK_TYPE.API_ERROR, CATEGORY.AUTH, {
        email,
        enforcedMfa,
        error,
      });
    }
  }

  reset() {
    this.tier = Tier.NONE;
    this.enforcedMfa = false;
    this.id = '';
    this.name = '';
  }

  prettyPrintTier() {
    if (this.isEnterprise) {
      return 'Enterprise';
    }
    if (this.isPro) {
      return 'Pro';
    }
    if (this.isFreeTrial) {
      return 'Free-trial';
    }
    if (this.isOpenSource) {
      return 'Open-source';
    }
    return 'N/A';
  }
}
